import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';

import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { ResumeBillingParams } from '../../hooks/useResumeBilling';
import { initialValues, validationSchema } from './validation';
import Spinner from '@/components/common/Spinner/Spinner';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';

interface IResumeBillingModalProps {
  onClose: () => void;
  resumeBilling: (inputParams: ResumeBillingParams) => void;
  billingData: Contract['billing'];
  isLoadingReasonCodes?: boolean;
}

export default function ResumeBillingModal({
  onClose,
  resumeBilling,
  billingData,
  isLoadingReasonCodes
}: IResumeBillingModalProps) {
  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);
  const { values, setFieldValue, errors, isValid, setFieldTouched, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => undefined,
    validateOnMount: true
  });

  const onResumeBilling = () => {
    resumeBilling({
      comment: values.comment
    });
  };

  if (isWarningVisible) {
    return (
      <AgConfirmationModal
        isOpen={true}
        title="You are about to resume Billing for this contract"
        onClose={onClose}
        onConfirm={onResumeBilling}
        contentStyle={{ width: 680 }}
        isConfirmButtonDisabled={isLoadingReasonCodes}
      >
        {isLoadingReasonCodes && (
          <div className="mt-50 mb-50 d-flex justify-content-center">
            <Spinner />
          </div>
        )}

        {!isLoadingReasonCodes && (
          <>
            <b>Please confirm if you want to proceed further and resume billing for the contract.</b>
            <br />
            <br />
            Pause Billing End Date: <b>{dayjs(billingData.currentStatusEndDate).format(DEFAULT_DATE_PATTERN)}</b>
            <br />
            Reasone Name: <b>{billingData.reasonCode?.name}</b>
            <br />
            Date Billing was Paused: <b>{dayjs(billingData.statusUpdated).format(DEFAULT_DATE_PATTERN)}</b>
          </>
        )}
      </AgConfirmationModal>
    );
  }

  return (
    <AgModalWindow
      title="Resume Billing"
      isOpen={true}
      onClose={onClose}
      contentStyle={{ width: 340 }}
    >
      <ModalFormTextarea
        id="comment"
        labelTitle="Comment"
        placeholder="Enter short comments"
        value={values.comment ?? undefined}
        onChange={(event) => {
          setFieldTouched('comment', true, false);
          setFieldValue('comment', event.target.value);
        }}
        validationMessage={
          errors?.comment && touched.comment ? errors.comment : undefined
        }
      />

      <ActionButtonsWrapper
        leftButton={(
          <Button
            buttonStyleType={ButtonStyleType.SECONDARY}
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
        rightButton={(
          <Button
            disabled={!isValid}
            buttonStyleType={ButtonStyleType.PRIMARY}
            onClick={() => setIsWarningVisible(true)}
          >
            Save
          </Button>
        )}
      />
    </AgModalWindow>
  );
}
